import React, { useEffect, useRef, useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Checkbox, FormControlLabel, Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useLicitacoesState } from "../context/licitacoes-context";
import { useLicitacoesController } from "../context/licitacoes-controller";
import PlainTextArea from "../../../components/plain-text-area";
import { LicitacaoDocumentosSection } from "./licitacao-documentos-section";
import { LicitacaoItensSection } from "./licitacao-itens-section";
import { LicitacaoParticipantesSection } from "./licitacao-participantes-section";
import { SelectWithQuickAdd } from "../../../components/select-with-quick-add";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { CurrencyInput } from "../../../components/currency-input";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";

export const LicitacaoForm = () => {
    const { dataRequest, orgaosList, modalidadesList, fasesList, situacoesList } = useLicitacoesState();
    const { handleOnChangeForm, loadLookups } = useLicitacoesController();
    const firstInputRef = useRef<HTMLInputElement>(null);
    // Não usar "dataRequest?.id" (truthy) aqui: um registro cujo Id real seja 0
    // seria tratado como "novo" e essas seções sumiriam ao editá-lo.
    const isExistingRecord = dataRequest?.id !== undefined && dataRequest?.id !== null;
    const [semValor, setSemValor] = useState<boolean>(false);

    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        (async () => {
            await loadLookups();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSemValor(!dataRequest?.valorEstimado && !!dataRequest?.textoJustificativaValorEstimado);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataRequest?.id]);

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item lg={12} sm={12} xl={12}>
                        <Alert severity="info">
                            Se Órgão ainda não existir, cadastre-o primeiro no menu lateral. Modalidade e Fase podem ser adicionadas na hora pelo botão "+" ao lado do respectivo campo.
                        </Alert>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Órgão *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idOrgao}
                            value={dataRequest?.idOrgao ?? ''}
                            onChange={(e) => handleOnChangeForm('idOrgao', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {orgaosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Número da Licitação *</InputLabel>
                        <TextInputWithCounter
                            ref={firstInputRef}
                            size="small"
                            maxLength={20}
                            error={!dataRequest?.numLicitacao}
                            value={dataRequest?.numLicitacao || ''}
                            onChange={(e) => handleOnChangeForm('numLicitacao', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <SelectWithQuickAdd
                            label="Modalidade *"
                            error={!dataRequest?.idModalidadeLicitacao}
                            value={dataRequest?.idModalidadeLicitacao ?? ''}
                            onChange={(value) => handleOnChangeForm('idModalidadeLicitacao', value)}
                            options={modalidadesList}
                            addLabel="Nova Modalidade"
                            onAdd={async (nome) => {
                                await AdminPortalAppService.addUpdateModalidadeLicitacao({ nomModalidadeLicitacao: nome });
                                await loadLookups();
                            }}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <SelectWithQuickAdd
                            label="Fase *"
                            error={!dataRequest?.idFaseLicitacao}
                            value={dataRequest?.idFaseLicitacao ?? ''}
                            onChange={(value) => handleOnChangeForm('idFaseLicitacao', value)}
                            options={fasesList}
                            addLabel="Nova Fase"
                            onAdd={async (nome) => {
                                await AdminPortalAppService.addUpdateFaseLicitacao({ nomFaseLicitacao: nome });
                                await loadLookups();
                            }}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Situação *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idSituacaoLicitacao}
                            value={dataRequest?.idSituacaoLicitacao ?? ''}
                            onChange={(e) => handleOnChangeForm('idSituacaoLicitacao', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {situacoesList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Nº Processo Administrativo</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={20}
                            value={dataRequest?.numProcessoAdministrativo || ''}
                            onChange={(e) => handleOnChangeForm('numProcessoAdministrativo', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Nº Edital</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={15}
                            value={dataRequest?.numEdital || ''}
                            onChange={(e) => handleOnChangeForm('numEdital', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Data/Hora Abertura *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaHoraAbertura } }}
                            value={dataRequest?.dtaHoraAbertura ? dayjs(dataRequest.dtaHoraAbertura) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaHoraAbertura', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Local de Abertura</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            value={dataRequest?.localAberturaLicitacao || ''}
                            onChange={(e) => handleOnChangeForm('localAberturaLicitacao', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Objeto *</InputLabel>
                        <PlainTextArea
                            size="small"
                            rows={5}
                            error={!dataRequest?.textoObjetoLicitacao}
                            value={dataRequest?.textoObjetoLicitacao || ''}
                            onChange={(e: any) => handleOnChangeForm('textoObjetoLicitacao', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Valor Estimado {!semValor && '*'}</InputLabel>
                        <CurrencyInput
                            size="small"
                            disabled={semValor}
                            error={!semValor && (!dataRequest?.valorEstimado || dataRequest.valorEstimado <= 0)}
                            value={semValor ? undefined : dataRequest?.valorEstimado}
                            onChange={(value) => handleOnChangeForm('valorEstimado', value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={3} sm={4} xl={3} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={semValor}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSemValor(checked);
                                        if (checked) {
                                            handleOnChangeForm('valorEstimado', undefined);
                                        } else {
                                            handleOnChangeForm('textoJustificativaValorEstimado', '');
                                        }
                                    }}
                                />
                            }
                            label="Sem valor"
                        />
                    </Grid>

                    <Grid item lg={5} sm={4} xl={5} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <FormControlLabel
                            control={<Checkbox checked={!!dataRequest?.indicativoRegistroPreco} onChange={(e) => handleOnChangeForm('indicativoRegistroPreco', e.target.checked)} />}
                            label="Registro de Preço"
                        />
                    </Grid>

                    {semValor && (
                        <Grid item lg={12} sm={12} xl={12}>
                            <InputLabel>Justificativa (ex: Menor valor global) *</InputLabel>
                            <TextInputWithCounter
                                size="small"
                                error={!dataRequest?.textoJustificativaValorEstimado}
                                value={dataRequest?.textoJustificativaValorEstimado || ''}
                                onChange={(e) => handleOnChangeForm('textoJustificativaValorEstimado', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>PNCP Url</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={dataRequest?.pncpUrl || ''}
                            onChange={(e) => handleOnChangeForm('pncpUrl', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {isExistingRecord && (
                        <React.Fragment>
                            <Grid item lg={4} sm={4} xl={4}>
                                <InputLabel>Data Homologação</InputLabel>
                                <DesktopDatePicker
                                    format='DD/MM/YYYY'
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    value={dataRequest?.dtaHomologacao ? dayjs(dataRequest.dtaHomologacao) : null}
                                    onChange={(value: Dayjs | null) => handleOnChangeForm('dtaHomologacao', value?.toISOString() ?? null)}
                                />
                            </Grid>
                            <Grid item lg={4} sm={4} xl={4}>
                                <InputLabel>Valor Homologado</InputLabel>
                                <CurrencyInput
                                    size="small"
                                    value={dataRequest?.valorHomologado}
                                    onChange={(value) => handleOnChangeForm('valorHomologado', value === 0 ? null : value)}
                                    fullWidth
                                />
                            </Grid>
                        </React.Fragment>
                    )}

                    {isExistingRecord ? (
                        <React.Fragment>
                            <LicitacaoDocumentosSection />
                            <LicitacaoItensSection />
                            <LicitacaoParticipantesSection />
                        </React.Fragment>
                    ) : (
                        <Grid item lg={12} sm={12} xl={12}>
                            <Alert severity="info">
                                Salve a licitação primeiro para liberar o cadastro de Documentos, Itens e Participantes.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </LocalizationProvider>
        </React.Fragment>
    );
};
